import type {
	Distributor,
	Role,
	ScopeType,
	Site,
	Space,
	Station,
} from "@soft-db/monitoring-api-client";
import { acceptHMRUpdate, defineStore } from "pinia";
import { RouteLocationGeneric } from "vue-router";
import { useCurrentUser, useCurrentUserRole } from "../api/current-user";

export type Wildcard = "*";

export type Resource =
	| "station"
	| "site"
	| "alert"
	| "report"
	| "space"
	| "space.site"
	| "space.station"
	| "distributor"
	| "user"
	| "public-page"
	| "dashboard"
	| "dashboard.own"
	| Wildcard;

export type Page = "integrity" | "audit" | "public-api" | "private-api";

type PermissionByField<T extends string> = Partial<Record<T, Role[]>>;

export type ResourceFields = {
	station: PermissionByField<keyof Station>;
	site: PermissionByField<keyof Site>;
	space: PermissionByField<keyof Space>;
	distributor: PermissionByField<keyof Distributor>;
};

export type Action = "get" | "subscribe" | "update" | "create" | "delete" | Wildcard;

export type Permission = `${Resource}.${Action}` | "station.link" | `${Page}.get`;

export class PermissionSet {
	permissions: readonly Permission[];

	constructor(permissions: Permission[]) {
		this.permissions = permissions;

		Object.freeze(this);
		Object.freeze(this.permissions);
		Object.seal(this);
	}

	has(required: Permission) {
		for (const permission of this.permissions) {
			if (this.comparePermission(required, permission)) {
				return true;
			}
		}

		return false;
	}

	readPermission(permission: Permission): [Resource, Action] {
		const splitted = permission.split(".");

		const action = splitted.at(-1) as Action;
		const resource = splitted.slice(0, -1) as string[];

		return [resource.join(".") as Resource, action];
	}

	comparePermission(a: Permission, b: Permission): boolean {
		const [aRes, aAct] = this.readPermission(a);
		const [bRes, bAct] = this.readPermission(b);

		return (
			(aRes === "*" || bRes === "*" || aRes === bRes) &&
			(aAct === "*" || bAct === "*" || aAct === bAct)
		);
	}
}

const EmptyPermissionSet = new PermissionSet([]);

function flat(permissions: Permission[][]): Permission[] {
	return permissions.flat();
}

export const distRolePermissions: Record<Role, PermissionSet> = {
	sys_admin: new PermissionSet(["*.*"]),
	admin: new PermissionSet(
		flat([
			["distributor.get", "distributor.update"],
			["user.*"],
			["public-page.*"],
			["report.update", "report.get", "report.subscribe"],
			["site.*"],
			["alert.*"],
			["space.*", "space.site.*", "space.station.*"],
			["dashboard.*", "dashboard.own.*"],
			["station.get", "station.update", "station.subscribe", "station.link"],
			["integrity.get", "audit.get"],
		])
	),
	editor: new PermissionSet(
		flat([
			["distributor.get"],
			["user.get"],
			["station.get", "station.subscribe", "station.update", "station.link"],
			["space.*"],
			["public-page.*"],
			["report.update", "report.get", "report.subscribe"],
			["site.*"],
			["alert.*"],
			["dashboard.*", "dashboard.own.*"],
			["integrity.get"],
		])
	),
	viewer: new PermissionSet(
		flat([
			["distributor.get"],
			["site.get"],
			["alert.get"],
			["space.get"],
			["report.get", "report.subscribe"],
			["dashboard.get", "dashboard.own.*"],
			["station.get", "station.subscribe"],
			["user.get"],
			["integrity.get"],
		])
	),
};

export const spaceRolePermission: Record<Role, PermissionSet> = {
	sys_admin: new PermissionSet(["*.*"]),
	admin: new PermissionSet(
		flat([
			["user.*"],
			["public-page.*"],
			["report.get", "report.update", "report.subscribe"],
			["site.get", "site.update", "site.subscribe", "site.create"],
			["station.get", "station.link"],
			["alert.*"],
			["space.get"],
			["dashboard.*", "dashboard.own.*"],
			["integrity.get", "audit.get"],
		])
	),
	editor: new PermissionSet(
		flat([
			["space.get"],
			["site.get", "site.update", "site.subscribe"],
			["report.get", "report.update", "report.subscribe"],
			["alert.*"],
			["alert.get", "alert.subscribe"],
			["dashboard.*", "dashboard.own.*"],
			["public-page.*"],
			["user.get"],
			["integrity.get"],
		])
	),
	viewer: new PermissionSet(
		flat([
			["space.get"],
			["report.get", "report.subscribe"],
			["site.get", "site.subscribe"],
			["alert.get", "alert.subscribe"],
			["dashboard.get", "dashboard.own.*"],
			["user.get"],
			["integrity.get"],
		])
	),
};

const publicPagePermissions: PermissionSet = new PermissionSet([
	"site.get",
	"dashboard.get",
	"dashboard.own.get",
]);

const FieldEditPermission: Partial<ResourceFields> = {
	station: {
		serialNumber: ["sys_admin"],
	},
};

export function resolvePermissions(scope: ScopeType | "public", role: Role) {
	switch (scope) {
		case "distributor":
			return distRolePermissions[role];
		case "space":
			return spaceRolePermission[role];
		case "public":
			return publicPagePermissions;
		case "none":
			return EmptyPermissionSet;
	}
}

export const usePermissionStore = defineStore("permission", () => {
	const { hasRole } = useCurrentUserRole();
	const { currentUser, isPublic } = useCurrentUser();

	const permissions = computed<PermissionSet>(() => {
		if (currentUser.value == null) {
			return EmptyPermissionSet;
		}

		const { role, scope } = currentUser.value;
		return resolvePermissions(isPublic.value ? "public" : scope.type, role);
	});

	const restrictions = computed<PermissionSet>(() => {
		if (currentUser.value == null) {
			return EmptyPermissionSet;
		}

		if (isPublic.value) {
			return EmptyPermissionSet;
		}

		const { features, scope } = currentUser.value;
		if (scope.type !== "space") {
			return EmptyPermissionSet;
		}

		const restrictions: Permission[] = [];
		if (!features.dashboard) {
			restrictions.push("dashboard.*");
		}

		return new PermissionSet(restrictions);
	});

	function hasPermission(required: Permission) {
		if (currentUser.value == null) {
			return false;
		}

		if (restrictions.value.has(required)) {
			return false;
		}

		if (permissions.value.has(required)) {
			return true;
		}

		return false;
	}

	function hasFieldEditPermission<T extends keyof ResourceFields>(
		resource: T,
		field: keyof ResourceFields[T]
	): boolean {
		const roles = FieldEditPermission[resource]?.[field as any] as Role[] | undefined;
		if (roles === undefined) {
			// No restriction, allow it
			return true;
		}

		for (const role of roles) {
			if (hasRole(role)) {
				return true;
			}
		}

		return false;
	}

	function hasUserAccessToRoute(route: RouteLocationGeneric): boolean {
		const { permission } = route.meta;

		if (permission == null) {
			return true;
		}

		if (Array.isArray(permission)) {
			return permission.some((p) => hasPermission(p));
		} else {
			return hasPermission(permission);
		}
	}

	return {
		permissions,
		hasPermission,
		hasFieldEditPermission,
		hasUserAccessToRoute,
	};
});

if (import.meta.hot) {
	import.meta.hot.accept(acceptHMRUpdate(usePermissionStore, import.meta.hot));
}
